import shopAPI from '@/api/shop'

// 辅助函数：将分类树扁平化为数组
const flattenCategoryTree = (categoryTree) => {
    const result = []

    const traverse = (categories) => {
        categories.forEach(category => {
            result.push(category)
            if (category.children && category.children.length > 0) {
                traverse(category.children)
            }
        })
    }

    traverse(categoryTree)
    return result
}

const state = {
    categories: [],
    categoryTree: [], // 新增：分类树形结构
    products: [],
    notices: [],
    settings: {},
    loading: false
}

const mutations = {
    SET_LOADING(state, loading) {
        state.loading = loading
    },
    SET_CATEGORIES(state, categories) {
        state.categories = categories
    },
    SET_CATEGORY_TREE(state, categoryTree) {
        state.categoryTree = categoryTree
    },
    SET_PRODUCTS(state, products) {
        state.products = products
    },
    SET_NOTICES(state, notices) {
        state.notices = notices
    },
    SET_SETTINGS(state, settings) {
        // 将设置数组转换为对象
        const settingsObj = {}
        settings.forEach(setting => {
            settingsObj[setting.settingKey] = setting.settingValue
        })
        state.settings = settingsObj
    }
}

const actions = {
    async fetchCategories({ commit }) {
        try {
            commit('SET_LOADING', true)
            const response = await shopAPI.getCategories()
            if (response.code === 200) {
                // 确保数据是数组，如果为空则设置为空数组
                const categories = Array.isArray(response.data) ? response.data : []
                commit('SET_CATEGORIES', categories)
            } else {
                console.warn('获取分类数据返回错误:', response)
                commit('SET_CATEGORIES', [])
            }
        } catch (error) {
            console.error('获取分类失败:', error)
            commit('SET_CATEGORIES', [])
        } finally {
            commit('SET_LOADING', false)
        }
    },

    // 新增：获取分类树形结构
    async fetchCategoryTree({ commit }) {
        try {
            commit('SET_LOADING', true)
            const response = await shopAPI.getCategoryTree()
            if (response.code === 200) {
                const categoryTree = Array.isArray(response.data) ? response.data : []
                commit('SET_CATEGORY_TREE', categoryTree)

                // 同时更新平面分类列表（向后兼容）
                const flatCategories = flattenCategoryTree(categoryTree)
                commit('SET_CATEGORIES', flatCategories)

            } else {
                console.warn('获取分类树数据返回错误:', response)
                commit('SET_CATEGORY_TREE', [])
                commit('SET_CATEGORIES', [])
            }
        } catch (error) {
            console.error('获取分类树失败:', error)
            commit('SET_CATEGORY_TREE', [])
            commit('SET_CATEGORIES', [])
        } finally {
            commit('SET_LOADING', false)
        }
    },

    // 新增：获取顶级分类
    async fetchTopLevelCategories({ commit }) {
        try {
            const response = await shopAPI.getTopLevelCategories()
            if (response.code === 200) {
                return response.data
            }
        } catch (error) {
            console.error('获取顶级分类失败:', error)
            return []
        }
    },

    // 新增：根据父分类ID获取子分类
    async fetchCategoriesByParentId({ commit }, parentId) {
        try {
            const response = await shopAPI.getCategoriesByParentId(parentId)
            if (response.code === 200) {
                return response.data
            }
        } catch (error) {
            console.error('获取子分类失败:', error)
            return []
        }
    },

    async fetchProductsByCategory({ commit }, categoryId) {
        try {
            // 验证categoryId是否有效
            if (!categoryId || categoryId === null || categoryId === undefined) {
                console.error('无效的分类ID:', categoryId)
                return
            }

            commit('SET_LOADING', true)
            const response = await shopAPI.getProductsByCategory(categoryId)
            if (response.code === 200) {
                commit('SET_PRODUCTS', response.data)
            }
        } catch (error) {
            console.error('获取商品失败:', error)
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async fetchHotProducts({ commit }, limit = 8) {
        try {
            const response = await shopAPI.getHotProducts(limit)
            if (response.code === 200) {
                return response.data
            }
        } catch (error) {
            console.error('获取热门商品失败:', error)
            return []
        }
    },

    async fetchNewProducts({ commit }, limit = 8) {
        try {
            const response = await shopAPI.getNewProducts(limit)
            if (response.code === 200) {
                return response.data
            }
        } catch (error) {
            console.error('获取新品失败:', error)
            return []
        }
    },

    async fetchNotices({ commit }) {
        try {
            const response = await shopAPI.getNotices()
            if (response.code === 200) {
                commit('SET_NOTICES', response.data)
            }
        } catch (error) {
            console.error('获取公告失败:', error)
        }
    },

    async fetchSettings({ commit }) {
        try {
            const response = await shopAPI.getSettings()
            if (response.code === 200) {
                commit('SET_SETTINGS', response.data)
            }
        } catch (error) {
            console.error('获取设置失败:', error)
        }
    }
}

const getters = {
    categories: state => state.categories,
    categoryTree: state => state.categoryTree, // 新增：分类树形结构
    products: state => state.products,
    notices: state => state.notices,
    settings: state => state.settings,
    loading: state => state.loading,
    shopName: state => state.settings.shop_name || '拾光',
    shopNameEn: state => state.settings.shop_name_en || 'Shiguang',
    // 新增：获取顶级分类
    topLevelCategories: state => {
        return state.categoryTree.filter(category => !category.parentId || category.parentId === 0)
    },
    // 新增：根据父分类ID获取子分类
    getChildrenByParentId: state => parentId => {
        const parent = state.categories.find(cat => cat.categoryId === parentId)
        return parent && parent.children ? parent.children : []
    }
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
}
